
import { wait } from '../wait/wait';
import { CancelError } from '../cancel/CancelError';
import { ILoader, ILoaderProps } from './ILoaderDecorator';

/**
 * Options for the `withRetries` loader decorator.
 * @public
 */
export interface IWithRetriesOptions {
  retryCount?: number;
  retryInterval?: number;
}

/**
 * Wraps a loader with a version which retries the original loader up to some number of additional times,
 * waiting for a specified interval between each attempt.
 * If all attempts fail, the final error is rethrown.
 * If the `CancelToken` passed to the produced loader is invoked, the attempt is canceled without any additional retries.
 * @public
 */
export function withRetries<TResult>(load: ILoader<TResult>, options: IWithRetriesOptions = {}): ILoader<TResult> {
  const {
    retryInterval = 1000,
    retryCount = 3
  } = options;

  return async (props: ILoaderProps = {}): Promise<TResult> => {
    const {
      cancelToken
    } = props;

    let result: {
      value: TResult;
    } | undefined;

    let attemptsRemaining = retryCount;

    do {
      let error: Error | undefined;

      if (cancelToken) {
        cancelToken.throwIfCanceled();
      }

      try {
        result = {
          value: await load()
        };
      } catch (e) {
        if (e instanceof CancelError) {
          throw e;
        }

        error = e;
      }

      if (result) {
        return result.value;
      }

      if (attemptsRemaining <= 0) {
        throw error;
      }

      await wait(retryInterval, cancelToken);

      attemptsRemaining--;
      // tslint:disable-next-line:no-constant-condition
    } while (true);
  };
}
